import React, { useRef, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import numeral from 'numeral'
import { Skeleton } from '@radix-ui/themes'
import { Box } from 'components/layout/box'
import { Text } from 'components/typography/text'
import { ArrowIcon } from 'assets/icons/icons'
import { NavigatorSummaryInfoResponse } from 'api/navigator/fetchNavigatorSummaryInfo'
import { useStockWebSocket } from 'hooks/useStockWebSocket'
import { tokens } from 'style/theme.css'

import { SearchItem } from '../types'
import { StockOption } from './StockOption'
import * as styles from '../Helix.css'

interface StockContainerProps {
	stock: SearchItem
	selectedOptionIndex: number
	onSelect: (path: string) => void
	onKeyDown: (e: React.KeyboardEvent) => void
	searchQuery: string
	companyInfo?: NavigatorSummaryInfoResponse
	isCompanyInfoLoading: boolean
	stockOptions: any[]
}

export const StockContainer: React.FC<StockContainerProps> = React.memo(
	({
		stock,
		selectedOptionIndex,
		onSelect,
		onKeyDown,
		searchQuery,
		companyInfo,
		isCompanyInfoLoading,
		stockOptions
	}) => {
		const optionsRef = useRef<HTMLDivElement>(null)

		// Extract ticker from stock label (format: "Company Name (TICKER)")
		const tickerMatch = stock.label.match(/\(([A-Z0-9]+)\)/)
		const ticker = tickerMatch ? tickerMatch[1] : ''

		// Use WebSocket hook for real-time price updates
		const { currentPrice, ytdPerformance, isConnected } = useStockWebSocket({
			ticker,
			ytdPriceOpen: companyInfo?.ytd_price_open || 0,
			initialPrice: companyInfo?.price || 0,
			onPriceUpdate: price => {
				// You can add additional logic here if needed when price updates
				console.log(`Price updated for ${ticker}: ${price}`)
			}
		})

		const formattedYtdPerformance = numeral(ytdPerformance).format('0.00%')
		const isPositive = ytdPerformance >= 0

		// Initialize virtualizer for stock options
		const optionsVirtualizer = useVirtualizer({
			count: stockOptions.length,
			getScrollElement: () => optionsRef.current,
			estimateSize: () => 40,
			overscan: 5
		})

		// Scroll selected option into view when selectedOptionIndex changes
		useEffect(() => {
			if (stockOptions.length > 0 && optionsVirtualizer) {
				optionsVirtualizer.scrollToIndex(selectedOptionIndex, { align: 'center' })
			}
		}, [selectedOptionIndex, stockOptions.length, optionsVirtualizer])

		// Filter options based on search query
		const filteredOptions = searchQuery
			? stockOptions.filter(
					option =>
						option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
						option.keywords?.some((keyword: string) => keyword.toLowerCase().includes(searchQuery.toLowerCase()))
			  )
			: stockOptions

		return (
			<div className={styles.stockOptionsContainer}>
				<div className={styles.stockOptionsInfoContainer}>
					<Skeleton className={styles.stockOptionsInfoLogoSkeleton} loading={isCompanyInfoLoading}>
						{!isCompanyInfoLoading && (
							<img
								src={
									companyInfo?.logo && !companyInfo.logo.startsWith('data:')
										? `data:image/png;base64,${companyInfo.logo}`
										: companyInfo?.logo
								}
								alt={companyInfo?.company_name}
								className={styles.stockOptionsInfoLogo}
							/>
						)}
					</Skeleton>
					<div className={styles.stockOptionsContent}>
						<Box display="flex" flexDirection="column">
							<Skeleton loading={isCompanyInfoLoading || !companyInfo}>
								<Text variant="metadata" color="neutral.300" style={{ minHeight: '16px' }}>
									{companyInfo?.sector}
								</Text>
							</Skeleton>
							<div style={{ marginTop: 'auto' }}>
								<Skeleton loading={isCompanyInfoLoading || !companyInfo}>
									<Text variant="metadata" color="neutral.300" style={{ minHeight: '16px' }}>
										{companyInfo?.company_name}
									</Text>
								</Skeleton>
								<Skeleton loading={isCompanyInfoLoading || !companyInfo}>
									<div
										className={styles.tickerContainer}
										style={
											{
												'--company-logo-url':
													companyInfo?.logo && !companyInfo.logo.startsWith('data:')
														? `url(data:image/png;base64,${companyInfo.logo})`
														: companyInfo?.logo
														? `url(${companyInfo.logo})`
														: 'none'
											} as React.CSSProperties
										}>
										<Text variant="body1Bold" color="neutral.100" className={styles.tickerText}>
											{ticker}
										</Text>
									</div>
								</Skeleton>
							</div>
						</Box>
						{companyInfo && (
							<Box display="flex" flexDirection="column" alignItems="flex-end">
								<Skeleton loading={isCompanyInfoLoading || !companyInfo}>
									<Text variant="body2" color="neutral.100" fontSize="xbig" style={{ minHeight: '20px' }}>
										{numeral(parseFloat(isConnected ? currentPrice : companyInfo.price)).format('$0,0.00')}
									</Text>
								</Skeleton>
								<Skeleton loading={isCompanyInfoLoading || !companyInfo}>
									<div className={styles.ytdPerformance}>
										<Text variant="body2" color="neutral.300" fontSize="xxsmall">
											YTD
										</Text>
										<Box display="flex" alignItems="center">
											<ArrowIcon
												width={16}
												height={16}
												isActive={isPositive}
												color={isPositive ? tokens.colors['success.500'] : tokens.colors['destructive.600']}
											/>
											<Text
												variant="body2"
												style={{ minHeight: '20px' }}
												color={isPositive ? 'success.500' : 'destructive.600'}>
												{formattedYtdPerformance}
											</Text>
										</Box>
									</div>
								</Skeleton>
							</Box>
						)}
					</div>
				</div>
				<div className={styles.stockOptionsList} ref={optionsRef} style={{ height: '300px', overflow: 'auto' }}>
					{filteredOptions.length > 0 ? (
						<div
							style={{
								height: `${optionsVirtualizer.getTotalSize()}px`,
								width: '100%',
								position: 'relative'
							}}>
							{optionsVirtualizer.getVirtualItems().map(virtualRow => {
								const option = filteredOptions[virtualRow.index]
								if (!option) return null
								return (
									<StockOption
										key={option.path}
										label={option.label}
										isSelected={virtualRow.index === selectedOptionIndex}
										onClick={() => onSelect(option.path)}
										onKeyDown={onKeyDown}
										icon={option.icon}
										type={option.type}
										style={{
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											height: `${virtualRow.size}px`,
											transform: `translateY(${virtualRow.start}px)`
										}}
									/>
								)
							})}
						</div>
					) : (
						<Text variant="body2" color="neutral.300" textAlign="center" style={{ padding: '10px 15px' }}>
							No options match your search
						</Text>
					)}
				</div>
			</div>
		)
	}
)

StockContainer.displayName = 'StockContainer'
